import { t, type TFunction } from "i18next";
import { z } from "zod";
import { PORT_CALL_FORM_FIELDS } from "../../constants";
import { hasPortCallDateConflict, type PortCallDateEntry } from "./portCallDateValidation";

const LANG_BASE_ADD_PORT_CALL = "vessel-call.last-ten-ports.sidebar.fields";
const MANDATORY_KEY = "form-validation.mandatory";

const requiredString = (label: string) => z.string().min(1, t(MANDATORY_KEY, { label }));

const requiredDate = (label: string) =>
  z.union([
    z.date({
      required_error: t(MANDATORY_KEY, { label }),
      invalid_type_error: t(MANDATORY_KEY, { label }),
    }),
    z.string().min(1, t(MANDATORY_KEY, { label })),
  ]);

export const toDate = (v: unknown): Date | null => {
  const d = v instanceof Date ? v : typeof v === "string" ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
};

export const normalizeToStartOfDay = (value: Date) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

type PortCallFormSchemaOptions = {
  enforceUniqueDates?: boolean;
  existingPortCalls?: PortCallDateEntry[];
  currentPortCallId?: string;
};

export const getPortCallFormSchema = (t: TFunction, options?: PortCallFormSchemaOptions) =>
  z
    .object({
      [PORT_CALL_FORM_FIELDS.PORT]: requiredString(t(`${LANG_BASE_ADD_PORT_CALL}.port`)),
      [PORT_CALL_FORM_FIELDS.COUNTRY]: requiredString(t(`${LANG_BASE_ADD_PORT_CALL}.country`)),
      [PORT_CALL_FORM_FIELDS.SECURITY_LEVEL_OF_PORT_CALL]: requiredString(
        t(`${LANG_BASE_ADD_PORT_CALL}.security-level`),
      ),
      [PORT_CALL_FORM_FIELDS.ARRIVAL_DATE]: requiredDate(
        t(`${LANG_BASE_ADD_PORT_CALL}.arrival-date`),
      ),
      [PORT_CALL_FORM_FIELDS.DEPARTURE_DATE]: requiredDate(
        t(`${LANG_BASE_ADD_PORT_CALL}.departure-date`),
      ),
      [PORT_CALL_FORM_FIELDS.COMMENTS]: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const arrivalDateLabel = t(`${LANG_BASE_ADD_PORT_CALL}.arrival-date`);
      const departureDateLabel = t(`${LANG_BASE_ADD_PORT_CALL}.departure-date`);
      const today = normalizeToStartOfDay(new Date());
      const arrivalDate = toDate(data[PORT_CALL_FORM_FIELDS.ARRIVAL_DATE]);
      const departureDate = toDate(data[PORT_CALL_FORM_FIELDS.DEPARTURE_DATE]);
      const normalizedArrivalDate = arrivalDate ? normalizeToStartOfDay(arrivalDate) : null;
      const normalizedDepartureDate = departureDate ? normalizeToStartOfDay(departureDate) : null;

      if (normalizedArrivalDate && normalizedArrivalDate.getTime() > today.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [PORT_CALL_FORM_FIELDS.ARRIVAL_DATE],
          message: t("form-validation.must-not-be-in-future", {
            label: arrivalDateLabel,
          }),
        });
      }

      if (normalizedDepartureDate && normalizedDepartureDate.getTime() > today.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [PORT_CALL_FORM_FIELDS.DEPARTURE_DATE],
          message: t("form-validation.must-not-be-in-future", {
            label: departureDateLabel,
          }),
        });
      }

      if (
        normalizedArrivalDate &&
        normalizedDepartureDate &&
        normalizedDepartureDate.getTime() < normalizedArrivalDate.getTime()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [PORT_CALL_FORM_FIELDS.DEPARTURE_DATE],
          message: t("form-validation.must-be-after", {
            subject: departureDateLabel,
            object: arrivalDateLabel,
          }),
        });
      }

      if (
        options?.enforceUniqueDates &&
        hasPortCallDateConflict({
          candidate: data,
          existingEntries: options.existingPortCalls,
          currentPortCallId: options.currentPortCallId,
        })
      ) {
        const message = t("vessel-call.last-ten-ports.validation.unique-date");

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [PORT_CALL_FORM_FIELDS.ARRIVAL_DATE],
          message,
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [PORT_CALL_FORM_FIELDS.DEPARTURE_DATE],
          message,
        });
      }
    });

export type PortCallsSchema = z.infer<ReturnType<typeof getPortCallFormSchema>>;
